import React, { useEffect, useMemo, useState } from "react";
import { db } from "../../../../firebase/firebaseConfig";
import { Button } from "../../../../components/ui/form/button";
import { collection, query, orderBy, limit, getDocs, doc, writeBatch, serverTimestamp } from "firebase/firestore";
import { toast } from "react-hot-toast";
import { logClassActivity } from "../../api/activityLogger";
import { FiSearch, FiSend, FiX } from "react-icons/fi";
import dayjs from "dayjs";

const todayStr = () => dayjs().format("YYYY-MM-DD");
const fmt = (d) => dayjs(d).format("DD/MM/YYYY");
const isOver = (deadline) => dayjs(deadline).endOf("day").isBefore(dayjs());

export default function NewAssignmentModal({ open, onClose, classInfo, students = [] }) {
  const [tests, setTests] = useState([]);
  const [loadingTests, setLoadingTests] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedTest, setSelectedTest] = useState(null);

  const [title, setTitle] = useState("");
  const [note, setNote] = useState("");
  const [startAt, setStartAt] = useState(todayStr());
  const [deadline, setDeadline] = useState(dayjs().add(7, "day").format("YYYY-MM-DD"));
  const [allowLate] = useState(false);
  const [maxAttempts] = useState(1);
  const [creating, setCreating] = useState(false);

  // NEW: published
  const [published, setPublished] = useState(false);

  // subset
  const [studentQuery, setStudentQuery] = useState("");
  const [selectedStudentIds, setSelectedStudentIds] = useState([]);
  const [assignToAll, setAssignToAll] = useState(true);

  const filteredStudents = useMemo(() => {
    const q = studentQuery.toLowerCase();
    if (!q) return students;
    return students.filter((s) => {
      const name = (s.fullName || `${s.firstName || ""} ${s.lastName || ""}`).toLowerCase();
      const email = (s.email || "").toLowerCase();
      return name.includes(q) || email.includes(q);
    });
  }, [students, studentQuery]);

  const allVisibleChecked = useMemo(() => {
    if (!filteredStudents.length) return false;
    return filteredStudents.every((s) => selectedStudentIds.includes(s.id));
  }, [filteredStudents, selectedStudentIds]);

  const handleToggleAllVisible = () => {
    if (allVisibleChecked) {
      const set = new Set(selectedStudentIds);
      filteredStudents.forEach((s) => set.delete(s.id));
      setSelectedStudentIds([...set]);
    } else {
      const set = new Set(selectedStudentIds);
      filteredStudents.forEach((s) => set.add(s.id));
      setSelectedStudentIds([...set]);
    }
  };

  const handleToggleOne = (id) => {
    setSelectedStudentIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  useEffect(() => {
    if (!open) return;
    const loadTests = async () => {
      try {
        setLoadingTests(true);
        const qRef = query(collection(db, "tests"), orderBy("createdAt", "desc"), limit(50));
        const snap = await getDocs(qRef);
        const arr = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setTests(arr);
      } catch (e) {
        console.error(e);
        toast.error("Không tải được danh sách bài kiểm tra.");
      } finally {
        setLoadingTests(false);
      }
    };
    loadTests();
  }, [open]);

  useEffect(() => {
    if (selectedTest && !title) setTitle(selectedTest.title || "");
  }, [selectedTest, title]);

  const filteredTests = useMemo(() => {
    const q = search.toLowerCase();
    if (!q) return tests;
    return tests.filter((t) => {
      const title = (t.title || "").toLowerCase();
      const level = (t.level || "").toLowerCase();
      const skill = (t.skill || "").toLowerCase();
      return title.includes(q) || level.includes(q) || skill.includes(q);
    });
  }, [tests, search]);

  const handleCreate = async () => {
    if (!selectedTest) return toast.error("Vui lòng chọn một bài kiểm tra.");
    if (!title?.trim()) return toast.error("Vui lòng nhập tiêu đề assignment.");
    if (!startAt || !deadline) return toast.error("Vui lòng chọn ngày giao và hạn nộp.");
    if (!assignToAll && selectedStudentIds.length === 0)
      return toast.error("Bạn đang giao theo subset — cần chọn ít nhất 1 học viên.");

    try {
      setCreating(true);
      const batch = writeBatch(db);
      const asgRef = doc(collection(db, "assignments"));

      const payload = {
        classId: classInfo.id,
        testId: selectedTest.id,
        title: title.trim(),
        note: note.trim(),
        startAt,
        deadline,
        allowLate,
        maxAttempts: Number(maxAttempts) || 1,
        status: isOver(deadline) ? "closed" : "open",
        createdBy: classInfo?.ownerId || "admin",
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        assignedTo: assignToAll ? null : selectedStudentIds,
        // NEW:
        published,
      };

      batch.set(asgRef, payload);

      // chỉ notify khi published
      if (published) {
        const targetStudents = assignToAll ? students : students.filter((s) => selectedStudentIds.includes(s.id));
        targetStudents.forEach((s) => {
          const notifRef = doc(collection(db, "notifications"));
          batch.set(notifRef, {
            toUserId: s.id,
            type: "assignment",
            title: "Bài tập mới",
            message: `Bạn có bài tập: ${payload.title} (hạn: ${fmt(deadline)})`,
            classId: classInfo.id,
            createdAt: serverTimestamp(),
          });
        });
      }

      await batch.commit();
      await logClassActivity({
        classId: classInfo.id,
        type: "assignment_created",
        severity: published ? "normal" : "warning",
        targetId: asgRef.id,
        targetTitle: payload.title,
        message: `${published ? "Đã giao" : "Đã tạo bản nháp"} bài tập: ${payload.title}${deadline ? ` · hạn ${fmt(deadline)}` : ""}.`,
        metadata: {
          assignmentId: asgRef.id,
          testId: selectedTest.id,
          deadline,
          published,
          targetMode: assignToAll ? "all" : "selected",
          targetCount: assignToAll ? students.length : selectedStudentIds.length,
        },
      });
      toast.success("Đã tạo assignment!");
      onClose?.();
    } catch (e) {
      console.error(e);
      toast.error("Tạo assignment thất bại.");
    } finally {
      setCreating(false);
    }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative z-50 w-[min(980px,95vw)] max-h-[90vh] overflow-hidden rounded-2xl bg-card border border-border shadow-xl">
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <h3 className="font-bold">Giao bài tập mới</h3>
          <button onClick={onClose} className="p-2 rounded-xl hover:bg-bg">
            <FiX />
          </button>
        </div>

        <div className="grid md:grid-cols-5 gap-4 p-4 overflow-y-auto" style={{ maxHeight: "75vh" }}>
          {/* Left: tests */}
          <div className="md:col-span-2 space-y-3">
            <div className="relative">
              <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 opacity-60" />
              <input
                className="w-full pl-9 pr-3 py-2 rounded-xl border border-border bg-bg"
                placeholder="Tìm test theo tiêu đề/level/skill..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            <div className="rounded-xl border border-border overflow-hidden">
              <div className="max-h-[48vh] overflow-y-auto divide-y divide-border">
                {loadingTests && <div className="p-3 text-sm text-text2">Đang tải tests...</div>}
                {!loadingTests && filteredTests.length === 0 && (
                  <div className="p-3 text-sm text-text2">Không có test phù hợp.</div>
                )}
                {filteredTests.map((t) => (
                  <button
                    key={t.id}
                    className={`w-full text-left p-3 hover:bg-primary/5 ${selectedTest?.id === t.id ? "bg-primary/10" : ""}`}
                    onClick={() => setSelectedTest(t)}
                  >
                    <div className="font-semibold">{t.title}</div>
                    <div className="text-xs text-text2">
                      {(t.level || "N/A")} • {(t.skill || t.type || "General")}
                      {t.duration ? ` • ${t.duration}′` : ""}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Right: config + subset */}
          <div className="md:col-span-3 space-y-3">
            <div>
              <label className="text-sm font-medium">Tiêu đề</label>
              <input
                className="w-full px-3 py-2 mt-1 rounded-xl border border-border bg-card"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Ví dụ: KET Listening Practice – Set 3"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Ghi chú (tuỳ chọn)</label>
              <textarea
                rows={3}
                className="w-full px-3 py-2 mt-1 rounded-xl border border-border bg-card"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Yêu cầu: hoàn thành trước 21:00, xem kỹ Part 2..."
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium">Ngày giao</label>
                <input
                  type="date"
                  value={startAt}
                  onChange={(e) => setStartAt(e.target.value)}
                  className="w-full px-3 py-2 mt-1 rounded-xl border border-border bg-card"
                />
              </div>
              <div>
                <label className="text-sm font-medium">Hạn nộp</label>
                <input
                  type="date"
                  value={deadline}
                  onChange={(e) => setDeadline(e.target.value)}
                  className="w-full px-3 py-2 mt-1 rounded-xl border border-border bg-card"
                />
              </div>
            </div>

            {/* NEW: Published toggle */}
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={published} onChange={(e) => setPublished(e.target.checked)} />
              Công khai (hiển thị cho học viên)
            </label>

            {/* Subset */}
            <div className="rounded-xl border border-border">
              <div className="px-3 py-2 border-b border-border flex items-center justify-between">
                <div className="text-sm font-medium">Giao cho</div>
                <label className="text-sm flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={assignToAll}
                    onChange={(e) => {
                      setAssignToAll(e.target.checked);
                      if (e.target.checked) setSelectedStudentIds([]);
                    }}
                  />
                  Toàn lớp
                </label>
              </div>

              {!assignToAll && (
                <div className="p-3 space-y-2">
                  <div className="flex items-center gap-2">
                    <input
                      className="flex-1 px-3 py-2 rounded-xl border border-border bg-bg"
                      placeholder="Tìm học viên theo tên/email..."
                      value={studentQuery}
                      onChange={(e) => setStudentQuery(e.target.value)}
                    />
                    <button
                      type="button"
                      onClick={handleToggleAllVisible}
                      className="px-3 py-2 rounded-xl border border-border hover:bg-primary/5 text-sm"
                    >
                      {allVisibleChecked ? "Bỏ chọn tất cả đang hiển thị" : "Chọn tất cả đang hiển thị"}
                    </button>
                  </div>

                  <div className="text-xs text-text2">
                    Đã chọn: <b>{selectedStudentIds.length}</b> / {students.length} học viên
                  </div>

                  <div className="max-h-[28vh] overflow-y-auto rounded-lg border border-dashed border-border">
                    {filteredStudents.length === 0 && (
                      <div className="p-3 text-sm text-text2">Không có học viên phù hợp.</div>
                    )}
                    {filteredStudents.map((stu) => {
                      const label = stu.fullName || `${stu.firstName || ""} ${stu.lastName || ""}`.trim();
                      const checked = selectedStudentIds.includes(stu.id);
                      return (
                        <label
                          key={stu.id}
                          className="flex items-center gap-2 px-3 py-2 border-b border-border last:border-0 hover:bg-primary/5 cursor-pointer"
                        >
                          <input type="checkbox" checked={checked} onChange={() => handleToggleOne(stu.id)} />
                          <span className="text-sm">{label}</span>
                          {stu.email && <span className="ml-auto text-xs text-text2">{stu.email}</span>}
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <Button onClick={onClose} className="rounded-xl bg-bg text-menu">
                Hủy
              </Button>
              <Button
                onClick={handleCreate}
                disabled={creating}
                className="rounded-xl bg-primary text-text-button hover:brightness-95 flex items-center gap-2"
              >
                <FiSend />
                {creating ? "Đang giao..." : "Giao bài tập"}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
